import * as yup from 'yup';
import { ArrowLeft, ArrowUpRight, Check, Info, User, Users } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { useForm } from 'react-form-state-manager';

import { AssignmentType, ParticipantAttribute } from '__generated__/graphql';

import { EmbedContext } from '../Common/EmbedProvider';
import { Event, ParticipantField, Ticket, getRequiredParticipantAttributes } from '../Checkout/helpers';
import { FeedbackForm } from '../../common/Feedback/FeedbackForm';
import { SectionTitle, TitleBar } from '../Common/Layout';
import {
  concatPrefix, emailRegex, filterPrefix, getServerErrors, mergeUrlParams,
} from '../../common/helpers';
import { getLoginUrl } from '../../common/Auth/useAuth';
import { getParticipantAttributesSchema } from '../../common/ParticipantAttributes/helpers';
import {
  getParticipantFieldEntrySchema, getPromotionFields, prefillFieldEntries,
} from '../../common/ParticipantFields/helpers';
import { prefillRegistrationCountry } from '../Checkout/StateProvider';
import { useEvent } from '../Common/EventProvider';
import AssignmentForm from '../../common/Registrations/AssignmentForm';
import InvitationCodeForm from '../Checkout/InvitationCodeForm';
import JoinWaitlistMutation from './JoinWaitlistMutation';
import StateContext from '../Checkout/StateContext';
import UI from '../../common/UI';
import config from '../config';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useUrlState from '../../common/useUrlState';
import useValidation from '../../common/useValidation';

interface WaitlistPageProps {
  ticketId: string;
}

interface Participant {
  email: string;
  first_name: string;
}

const WaitlistPage = ({ ticketId }: WaitlistPageProps) => {
  const { t } = useTranslation();
  const { event } = useEvent();

  useDocumentTitle([t('waitlist.waitlist'), event.title]);

  const ticket = event.ticket_categories
    .map((ticketCategory) => ticketCategory.tickets_for_sale.find((ticket) => ticket.id === ticketId))
    .find((ticket) => ticket);

  const [urlState] = useUrlState({
    parse: (urlState, { string }) => ({
      ...urlState,
      return: string(urlState.return),
    }),
  });

  const ticketCategory = event.ticket_categories.find(
    (ticketCategory) => ticketCategory.tickets_for_sale.map((ticket) => ticket.id).includes(ticket?.id),
  );

  const returnLink = urlState.return === 'resale'
    ? `/${event.id}/resale?ticket=${ticket?.id}`
    : `/${event.id}${mergeUrlParams({ showCategories: [ticketCategory?.id] })}`;

  const [participant, setParticipant] = useState<Participant>();

  return (
    <>
      <UI.Div>
        <TitleBar>
          <UI.Div>
            <UI.FadeIn>
              <UI.Link to={returnLink} title={t('back')} resetScrollPosition={false}>
                <UI.Icon>
                  <ArrowLeft />
                </UI.Icon>
              </UI.Link>
            </UI.FadeIn>
          </UI.Div>
          <UI.Div>
            {t('waitlist.waitlist')}
          </UI.Div>
          <UI.Div />
        </TitleBar>

        <UI.Card sc={{ hasPanels: true, active: true }}>
          {participant && (
            <UI.FadeIn>
              <UI.FormGrid sc={{ textAlign: 'center' }}>
                <UI.Badge sc={{ size: 48, brand: 'success' }} style={{ margin: '0 auto' }}>
                  <UI.Icon sc={{ size: 2.5 }}>
                    <Check />
                  </UI.Icon>
                </UI.Badge>

                <SectionTitle>
                  {t('success_page.thank_you', { name: participant.first_name })}
                </SectionTitle>

                <UI.Div style={{ maxWidth: 400, margin: '0 auto', textWrap: 'balance' }}>
                  {t('waitlist.success_message', { event: event.title })}
                </UI.Div>

                <UI.Div>
                  <UI.AButton
                    href={getLoginUrl({ email: participant.email })}
                    target="_blank"
                    rel="noreferer noopener"
                    sc={{ outline: true }}
                  >
                    <UI.Icon>
                      <User />
                    </UI.Icon>
                    {' '}
                    {t('success_page.go_to_dashboard')}
                  </UI.AButton>
                </UI.Div>
              </UI.FormGrid>
            </UI.FadeIn>
          )}
          {event.enable_waitlist && ticket && !participant && (
            <UI.FadeIn>
              <WaitListForm event={event} ticket={ticket} onSuccess={(participant) => setParticipant(participant)} />
            </UI.FadeIn>
          )}
          {(!event.enable_waitlist || !ticket) && (
            <UI.Div sc={{ textAlign: 'center' }}>
              {t('waitlist.disabled_message')}
            </UI.Div>
          )}
        </UI.Card>
      </UI.Div>

      {!participant && event.has_invitation_codes && (
        <InvitationCodeForm />
      )}

      {participant && (
        <FeedbackForm
          label={t('success_page.feedback_title')}
          eventId={event.id}
        />
      )}
    </>
  );
};

interface WaitListFormProps {
  event: Event;
  ticket: Ticket;
  onSuccess: (participant: Participant) => unknown;
}

const WaitListForm = ({ event, ticket, onSuccess }: WaitListFormProps) => {
  const { t } = useTranslation();
  const { scrollToCheckoutForm } = useContext(EmbedContext);
  const { formatNumber, locale } = useLocale();

  /** URL params on successfully placing an order. */
  const { form: { source_url: sourceUrl, initial_url: initialUrl, final_url: finalUrl } } = useContext(StateContext);

  const requireDetails = ticket.assignment_type === AssignmentType.required;
  const promotion = ticket.promotions_for_sale[0];

  const requiredParticipantFields = requireDetails
    ? getPromotionFields(event.enabled_participant_fields, promotion.id)
    : [];

  const termsText = event.terms_text;
  const termsUrl = event.terms_url || event.terms?.url;
  const requireTerms = termsText || termsUrl;

  const form = useForm({
    values: {
      registration: prefillRegistrationCountry({
        ticket: {
          id: ticket.id,
        },
        purchase: {
          promotion: {
            id: promotion.id,
          },
        },
        participant: {
          mail_opt_ins: [],
        },
        details: {},
        fields: prefillFieldEntries([], requiredParticipantFields),
        upgrades: [],
      }, event),
      terms: false,
    },
  });

  const requiredParticipantAttributes = requireDetails
    ? getRequiredParticipantAttributes(form.values.registration, event)
    : [];

  const scrollToErrorFields = () => {
    scrollToCheckoutForm();
    Object.keys(errors).forEach((key: string) => form.setTouched(key, true));
  };

  const [joinWaitlist, { error, loading }] = useMutation(JoinWaitlistMutation, {
    variables: {
      input: {
        registration: form.values.registration,
        invitation_code: event.invitation_code?.value,
        source_url: sourceUrl,
        initial_url: initialUrl,
        final_url: finalUrl,
      },
    },
    onCompleted: () => {
      onSuccess({
        first_name: form.values.registration.participant.first_name,
        email: form.values.registration.participant.email,
      });
    },
    onError: () => {
      scrollToErrorFields();
    },
  });

  const { valid, errors, updateCustomErrors, updateValidating } = useValidation({
    schema: getJoinWaitlistSchema(requiredParticipantFields, requiredParticipantAttributes, event),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => joinWaitlist()} id="CheckoutForm">
      <UI.FormGrid>
        <SectionTitle>
          {t('waitlist.join_waitlist')}
        </SectionTitle>

        {event.invitation_code?.invitation.description && (
          <UI.Info icon={<Info />}>
            <UI.HTML html={event.invitation_code?.invitation.description} />
          </UI.Info>
        )}

        <UI.Card sc={{ spacing: 'sm', outline: 'gray.200', background: 'gray.50' }}>
          <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <UI.H4>
              <UI.Icon>
                <UI.Icons.Ticket />
              </UI.Icon>
              {' '}
              {ticket.title}
            </UI.H4>
            <UI.Span sc={{ noWrap: true }}>
              {ticket.units > 1 && (
                <UI.Label>
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                  {' '}
                  {formatNumber(ticket.units)}
                </UI.Label>
              )}
              {ticket.is_sold_out && (
                <UI.Label sc={{ ml: 0.5 }}>
                  {t('selector.sold_out')}
                </UI.Label>
              )}
            </UI.Span>
          </UI.FlexContainer>
        </UI.Card>

        {error && (
          <UI.ServerErrors error={error} />
        )}

        <AssignmentForm
          registration={form.values.registration}
          onChange={(update) => form.set((values) => ({
            ...values,
            registration: {
              ...values.registration,
              ...update(values.registration),
              upgrades: values.registration.upgrades,
            },
          }))}
          onBlur={(field) => form.setTouched(`registration.${field}`, true)}
          touched={form.getTouched('registration')}
          registrationAttributes={requiredParticipantAttributes}
          errors={filterPrefix(errors, 'registration')}
          enabledParticipantFields={event.enabled_participant_fields}
          updateCustomErrors={(errors, prefix) => updateCustomErrors(errors, concatPrefix('registration', prefix))}
          updateValidating={(validating, prefix) => updateValidating(validating, concatPrefix('registration', prefix))}
        />

        {event.mail_permissions.map((mailPermission) => (
          <UI.InputGroup key={mailPermission.id}>
            <UI.InputLabel htmlFor={`MailPermission_${mailPermission.id}`}>
              {mailPermission.title}
            </UI.InputLabel>
            <UI.Checkbox
              {...form.checklist('registration.participant.mail_opt_ins', mailPermission.id)}
              id={`MailPermission_${mailPermission.id}`}
            >
              {mailPermission.label}
            </UI.Checkbox>
          </UI.InputGroup>
        ))}

        {requireTerms && (
          <UI.InputGroup id="TermsSection" sc={{ valid: !errors.terms, touched: form.getTouched('terms') }}>
            <UI.InputLabel>
              {t('terms_and_conditions')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            {termsUrl && (
              <UI.Checkbox {...form.checkbox('terms')}>
                <Trans i18nKey="agree_to_terms">
                  {/* eslint-disable */}
                  <a href={termsUrl} target="_blank" rel="noopener noreferrer" />
                  {/* eslint-enable */}
                </Trans>
              </UI.Checkbox>
            )}
            {termsText && (
              <UI.Checkbox {...form.checkbox('terms')}>
                <UI.HTML html={termsText} />
              </UI.Checkbox>
            )}
            <UI.ErrorMessages attribute={t('terms_and_conditions')} errors={errors.terms} />
          </UI.InputGroup>
        )}

        {!valid && (
          <UI.FadeIn>
            <UI.Warning>
              <UI.A
                onClick={() => scrollToErrorFields()}
                sc={{ secondary: true }}
                style={{ fontWeight: 500 }}
                role="button"
              >
                {t('fill_required_fields')}
                {' '}
                <UI.Icon>
                  <ArrowUpRight />
                </UI.Icon>
              </UI.A>
            </UI.Warning>
          </UI.FadeIn>
        )}

        <UI.HR />

        <UI.Div>
          <Trans
            i18nKey="common:terms.terms_notice"
            values={{
              action: t('waitlist.join_waitlist'),
            }}
          >
            <UI.A href={config.termsUrl[locale]} target="_blank" rel="noopener noreferer" />
          </Trans>
        </UI.Div>

        <UI.Button
          type="submit"
          sc={{ brand: 'secondary', block: true, loading }}
          disabled={loading || !valid}
        >
          <UI.Icon>
            <Check />
          </UI.Icon>
          {' '}
          {t('waitlist.join_waitlist')}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

const getJoinWaitlistSchema = (
  requiredFields: ParticipantField[],
  requiredAttributes: ParticipantAttribute[],
  event: Event,
) => {
  const fieldEntrySchema = getParticipantFieldEntrySchema(requiredFields);

  return yup.object({
    registration: yup.object({
      participant: yup.object({
        email: yup.string().required().matches(emailRegex, { name: 'email' }),
        first_name: yup.string().ensure().required(),
        last_name: yup.string().ensure().required(),
      }),
      details: getParticipantAttributesSchema(requiredAttributes),
      fields: yup.array(fieldEntrySchema),
    }),
    terms: yup.boolean().test('required', '', (value) => (
      event.terms || event.terms_url || event.terms_text ? value : true
    )),
  });
};

export default WaitlistPage;
